import React from 'react';
import classNames from 'classnames';

import { requiredFieldArrayCheckbox } from '../../../util/validators';
import { FieldCheckboxGroup } from '../../../components';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';

import css from './TermsAndConditions.module.css';

const KEY_CODE_ENTER = 13;

/**
 * A component that renders the terms and conditions.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onOpenTermsOfService - The function to open the terms of service modal
 * @param {Function} props.onOpenPrivacyPolicy - The function to open the privacy policy modal
 * @param {string} props.formId - The form id
 * @param {intlShape} props.intl - The intl object
 * @param {boolean} props.hasReadTos - Whether the user has read the terms of service
 * @param {boolean} props.hasReadPrivacy - Whether the user has read the privacy policy
 * @returns {JSX.Element}
 */
const TermsAndConditions = props => {
  const {
    onOpenTermsOfService,
    onOpenPrivacyPolicy,
    formId,
    intl,
    hasReadTos,
    hasReadPrivacy
  } = props;

  const isLocked = !hasReadTos || !hasReadPrivacy;

  const handleClick = callback => e => {
    e.preventDefault();
    e.stopPropagation(); 
    callback(e);
  };

  const handleKeyUp = callback => e => {
    // Allow click action with keyboard like with normal links
    if (e.keyCode === KEY_CODE_ENTER) {
      callback();
    }
  };

  const validateTerms = value => {
    if (!isLocked) {
      return intl.formatMessage({ id: 'AuthenticationPage.readDocumentsRequired' });
    }
    return requiredFieldArrayCheckbox(
      intl.formatMessage({ id: 'AuthenticationPage.termsAndConditionsAcceptRequired' })
    )(value);
  };

  const termsLink = (
    <span
      className={classNames(css.termsLink, { [css.unreadLink]: !hasReadTos })}
      onClick={handleClick(onOpenTermsOfService)}
      role="button"
      tabIndex="0"
      onKeyUp={handleKeyUp(onOpenTermsOfService)}
      title={!hasReadTos ? intl.formatMessage({ id: 'AuthenticationPage.readToSRequired' }) : ''}
    >
      <FormattedMessage id="AuthenticationPage.termsAndConditionsTermsLinkText" />
      {!hasReadTos && <span className={css.unreadIndicator}>*</span>}
    </span>
  );

  const privacyLink = (
    <span
      className={classNames(css.privacyLink, { [css.unreadLink]: !hasReadPrivacy })}
      onClick={handleClick(onOpenPrivacyPolicy)}
      role="button"
      tabIndex="0"
      onKeyUp={handleKeyUp(onOpenPrivacyPolicy)}
      title={!hasReadPrivacy ? intl.formatMessage({ id: 'AuthenticationPage.readPrivacyRequired' }) : ''}
    >
      <FormattedMessage id="AuthenticationPage.termsAndConditionsPrivacyLinkText" />
      {!hasReadPrivacy && <span className={css.unreadIndicator}>*</span>}
    </span>
  );

  // Apply a custom wrapper class to block checkbox interaction (pointer-events: none)
  // while explicitly keeping the ToS & Privacy links clickable via CSS.
  const rootClasses = classNames(css.root, {
    [css.disabledWrapper]: isLocked
  });

  return (
    <div className={rootClasses}>
      <FieldCheckboxGroup
        name="terms"
        id={formId ? `${formId}.terms-accepted` : 'terms-accepted'}
        optionLabelClassName={css.finePrint}
        options={[
          {
            key: 'tos-and-privacy',
            label: intl.formatMessage(
              { id: 'AuthenticationPage.termsAndConditionsAcceptText' },
              { termsLink, privacyLink }
            ),
          },
        ]}
        validate={validateTerms}
      />

      {isLocked && (
        <p className={css.readRequirementMessage}>
          <FormattedMessage 
            id="AuthenticationPage.readDocumentsRequired" 
            defaultMessage="Please read and scroll to the bottom of both documents." 
          />
        </p>
      )}
    </div>
  );
};

export default TermsAndConditions;
